import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import Spinner from './Spinner';
import { Grid } from '@mui/material';

const overlappingMarkerKey = (marker) => {
    const city = marker.city ? String(marker.city).trim().toLowerCase() : '';
    const state = marker.state ? String(marker.state).trim().toLowerCase() : '';
    if (city && state) {
        return `city:${city}|${state}`;
    }
    if (
        marker.latLong &&
        marker.latLong.coordinates &&
        marker.latLong.coordinates.length === 2
    ) {
        return (
            'geo:' +
            Number(marker.latLong.coordinates[0]).toFixed(3) +
            ',' +
            Number(marker.latLong.coordinates[1]).toFixed(3)
        );
    }
    return null;
};

const overlappingMarkerSlots = (markers) => {
    const groups = new Map();
    (markers || []).forEach((marker, index) => {
        const key = overlappingMarkerKey(marker);
        if (!key) {
            return;
        }
        if (!groups.has(key)) {
            groups.set(key, []);
        }
        groups.get(key).push(index);
    });

    const slots = new Map();
    groups.forEach((indexes) => {
        const count = indexes.length;
        if (count < 2) {
            return;
        }
        indexes.forEach((index, i) => {
            slots.set(index, { i, count });
        });
    });
    return slots;
};

const overlappingPinRadius = (zoom, count) => {
    // Keep a tight cluster through regional zoom so pins still read as one town.
    // Only ease them apart once the user is in close enough to pick one.
    const t = Math.max(0, Math.min(1, ((zoom || 4) - 7) / 4));
    const minR = 8;
    const maxR = 7 + Math.min(count, 5);
    return minR + (maxR - minR) * t;
};

const pinStackZIndex = (lat, offsetY) => {
    // Lower on the screen (south, or a downward pixel nudge) paints on top.
    // Hover/selected CSS uses 20000 so names always win.
    return Math.round((90 - Number(lat)) * 100 + (offsetY || 0) * 20);
};

const formatMarkerDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });
};

const markerHoverTitle = (marker) => {
    const name =
        marker.stageName ||
        `${marker.firstName || ''} ${marker.lastName || ''}`.trim();
    const date = formatMarkerDate(marker.bookingWhen);
    const place = [marker.city, marker.state].filter(Boolean).join(', ');
    const suffix = date || place;
    return suffix ? `${name} — ${suffix}` : name;
};

const markerViewEl = (marker, content) => {
    if (content && content.closest) {
        const view = content.closest('.yNHHyP-marker-view');
        if (view) {
            return view;
        }
    }
    if (
        content &&
        content.parentElement &&
        content.parentElement.parentElement
    ) {
        return content.parentElement.parentElement;
    }
    return marker && marker.element;
};

const applyPinStack = (marker, lat, offsetY, content) => {
    const z = pinStackZIndex(lat, offsetY);
    if (marker) {
        marker.zIndex = z;
    }
    const pinContent = content || (marker && marker.content);
    const view = markerViewEl(marker, pinContent);
    if (view && view.style) {
        view.style.removeProperty('z-index');
        view.style.setProperty('--pin-z', String(z));
        return;
    }
    requestAnimationFrame(() => {
        const lateView = markerViewEl(marker, pinContent);
        if (lateView && lateView.style) {
            lateView.style.removeProperty('z-index');
            lateView.style.setProperty('--pin-z', String(z));
        }
    });
};

const overlappingOffset = (slot, zoom) => {
    const radius = overlappingPinRadius(zoom, slot.count);
    const angle = (2 * Math.PI * slot.i) / slot.count - Math.PI / 2;
    return {
        x: Math.round(Math.cos(angle) * radius),
        y: Math.round(Math.sin(angle) * radius),
    };
};

const applyOverlappingPinOffset = (content, marker, slot, zoom, lat) => {
    if (!slot) {
        applyPinStack(marker, lat, 0, content);
        return;
    }
    const { x, y } = overlappingOffset(slot, zoom);
    if (content && content.style) {
        content.style.transform = 'translate(' + x + 'px, ' + y + 'px)';
    }
    applyPinStack(marker, lat, y, content);
};

const MapFailed = () => (
    <Grid className="mapFail">
        <p>
            Sorry, but unfortunately, the Artist map won’t load. Refreshing the
            page might maybe help.
        </p>
    </Grid>
);

class MapErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return <MapFailed />;
        }
        return this.props.children;
    }
}

MapErrorBoundary.propTypes = {
    children: PropTypes.node,
};

const clearAdvancedMarkers = (markers) => {
    (markers || []).forEach((marker) => {
        if (!marker) {
            return;
        }
        try {
            if (window.google?.maps?.event) {
                window.google.maps.event.clearInstanceListeners(marker);
            }
            marker.map = null;
        } catch {
            /* ignore teardown errors from a failed Maps load */
        }
    });
};

const render = (status) => {
    switch (status) {
        case Status.LOADING:
            return <Spinner />;
        case Status.FAILURE:
            return <MapFailed />;
        case Status.SUCCESS:
            // console.log('Map Success!');
            return <></>;
        default:
            return null; // Add a default case
    }
};

const MyMapComponent = ({
    center,
    zoom,
    markers,
    markerClick,
    radius,
    circleCenter,
    preferredArtists,
    offsetOverlapping,
    mapHeight,
    onPinClick,
}) => {
    const mapRef = useRef(null); // Initialize mapRef with null

    const [theMap, setTheMap] = useState(null); // Initialize theMap with null
    const [circleSet, setCircle] = useState(false);
    const [markersSet, setMarkers] = useState(false); // Initialize theMap with null
    const [clickedMarker, setClickedMarker] = useState();
    const [markerLibraryReady, setMarkerLibraryReady] = useState(false);
    const pinOffsetTargetsRef = useRef([]);
    const mapMarkersRef = useRef([]);
    const markersRef = useRef(markers);
    markersRef.current = markers;
    const markerIdsKey = (markers || [])
        .map((marker) => marker._id || marker.slug)
        .join(',');

    useEffect(() => {
        if (clickedMarker && typeof markerClick === 'function') {
            const newMarkersSelected = [
                ...(preferredArtists || []),
                clickedMarker,
            ];
            markerClick('domEvent', 'preferredArtists', newMarkersSelected);
        }
    }, [clickedMarker]);

    // console.log('theMap', theMap);

    useEffect(() => {
        const el = mapRef.current;
        if (!(el instanceof Element) || !window.google?.maps?.Map) {
            return undefined;
        }
        let map;
        try {
            map = new window.google.maps.Map(el, {
                center,
                zoom,
                mapId: 'dec1188be6451632',
                mapTypeControl: false,
                streetViewControl: false,
                // scrollwheel: false,
                gestureHandling: 'cooperative',
                clickableIcons: false, //Claude recommended this to keep city and state labels from being clickable - Dec 19,2024
            });
        } catch {
            return undefined;
        }
        setTheMap(map);
        return () => {
            clearAdvancedMarkers(mapMarkersRef.current);
            mapMarkersRef.current = [];
            pinOffsetTargetsRef.current = [];
            try {
                if (map && window.google?.maps?.event) {
                    window.google.maps.event.clearInstanceListeners(map);
                }
            } catch {
                /* ignore */
            }
            if (el instanceof Element) {
                el.replaceChildren();
            }
            setTheMap(null);
        };
    }, []);

    useEffect(() => {
        if (!theMap) {
            return undefined;
        }
        if (window.google?.maps?.marker?.AdvancedMarkerElement) {
            setMarkerLibraryReady(true);
            return undefined;
        }
        if (!window.google?.maps?.importLibrary) {
            return undefined;
        }
        let cancelled = false;
        window.google.maps
            .importLibrary('marker')
            .then(() => {
                if (
                    !cancelled &&
                    window.google?.maps?.marker?.AdvancedMarkerElement
                ) {
                    setMarkerLibraryReady(true);
                }
            })
            .catch(() => {
                /* marker library failed; leave markerLibraryReady false */
            });
        return () => {
            cancelled = true;
        };
    }, [theMap]);

    useEffect(() => {
        const teardown = () => {
            clearAdvancedMarkers(mapMarkersRef.current);
            mapMarkersRef.current = [];
            pinOffsetTargetsRef.current = [];
        };

        if (
            !theMap ||
            !markerLibraryReady ||
            !window.google?.maps?.marker?.PinElement ||
            !window.google?.maps?.marker?.AdvancedMarkerElement
        ) {
            return teardown;
        }

        teardown();

        const currentMarkers = markersRef.current;
        if (!currentMarkers || currentMarkers.length === 0) {
            setMarkers(true);
            return teardown;
        }

        try {
            const pinSlots = offsetOverlapping
                ? overlappingMarkerSlots(currentMarkers)
                : new Map();
            const mapZoom = theMap.getZoom ? theMap.getZoom() : zoom;
            const markersCoords = currentMarkers.map((marker, i) => {
                // console.log('markersCoords marker', marker);
                if (
                    marker.latLong &&
                    marker.latLong.coordinates &&
                    marker.latLong.coordinates.length === 2
                ) {
                    const icon = document.createElement('div');
                    icon.className = 'markerGlyph';
                    {
                        marker.squareImg || marker.profileImg
                            ? (icon.innerHTML =
                                  '<img src=' +
                                  (marker.squareImg || marker.profileImg) +
                                  ' class="artistImg" style="border-radius:100%; width: 18px; height: 18px; margin-top: 4px;"></img>')
                            : (icon.innerHTML = '<i class="fa fa-home"></i>');
                    }

                    const pinGlyph = new window.google.maps.marker.PinElement({
                        glyph: icon,
                        glyphColor: '#1C1E1C',
                        background:
                            marker.active === false
                                ? '#7a9492'
                                : marker.verified === true ||
                                    (Array.isArray(marker.verifiedByHosts) &&
                                        marker.verifiedByHosts.length > 0)
                                  ? '#6fbf73'
                                  : '#ffa14e',
                        borderColor: '#1C1E1C',
                        scale: 0.8,
                    });
                    const pinEl = pinGlyph && pinGlyph.element;
                    if (!(pinEl instanceof Element)) {
                        return undefined;
                    }

                    const pinLat = marker.latLong.coordinates[1];
                    const pinSlot = pinSlots.get(i);
                    const pinOffsetY = pinSlot
                        ? overlappingOffset(pinSlot, mapZoom).y
                        : 0;

                    const thisMarker =
                        new window.google.maps.marker.AdvancedMarkerElement({
                            map: theMap,
                            position: {
                                lat: pinLat,
                                lng: marker.latLong.coordinates[0],
                            },
                            zIndex: pinStackZIndex(pinLat, pinOffsetY),
                            content: pinEl,
                            title: markerHoverTitle(marker),
                            // icon: 'pin.png',
                        });
                    if (
                        thisMarker &&
                        thisMarker.content &&
                        thisMarker.content.classList
                    ) {
                        thisMarker.content.classList.add(
                            'marker' + marker.slug
                        );
                        if (marker.active === false) {
                            thisMarker.content.classList.add('inactiveHost');
                        }
                        if (
                            marker.active !== false &&
                            (marker.verified === true ||
                                (Array.isArray(marker.verifiedByHosts) &&
                                    marker.verifiedByHosts.length > 0))
                        ) {
                            thisMarker.content.classList.add(
                                'verifiedAttendee'
                            );
                        }
                        if (pinSlot) {
                            pinOffsetTargetsRef.current.push({
                                content: thisMarker.content,
                                marker: thisMarker,
                                slot: pinSlot,
                                lat: pinLat,
                            });
                        }
                        applyOverlappingPinOffset(
                            thisMarker.content,
                            thisMarker,
                            pinSlot,
                            mapZoom,
                            pinLat
                        );
                        // thisMarker.content.onclick = (e) => {
                        //     console.log(
                        //         marker.slug + ' marker content clicked'
                        //     );
                        //     onMarkerClick(e, marker);
                        // };

                        // console.log('thisMarker.content', thisMarker.content);
                        // console.log(marker.city + ' marker created.');

                        // Add a click listener for each marker
                        window.google.maps.event.addListener(
                            thisMarker,
                            'click',
                            ({ domEvent, latLng }) => {
                                //send the clicked marker back up to the parent component to use for an Autocomplete probably
                                if (
                                    !thisMarker.content.classList.contains(
                                        'selected'
                                    )
                                ) {
                                    // console.log('marker clicked', marker);
                                    // console.log('domEvent', domEvent);
                                    var elems =
                                        document.querySelectorAll('.selected');
                                    [].forEach.call(elems, function (el) {
                                        if (
                                            el.parentElement &&
                                            el.parentElement.parentElement
                                        ) {
                                            el.parentElement.parentElement.classList.remove(
                                                'markerSelected'
                                            );
                                        }
                                        el.classList.remove('selected');
                                    });
                                    thisMarker.content.classList.add(
                                        'selected'
                                    );
                                    if (
                                        thisMarker.content.parentElement &&
                                        thisMarker.content.parentElement
                                            .parentElement
                                    ) {
                                        thisMarker.content.parentElement.parentElement.classList.add(
                                            'markerSelected'
                                        );
                                    }
                                    setClickedMarker(marker);
                                    if (typeof onPinClick === 'function') {
                                        onPinClick(marker);
                                    }
                                }

                                // onMarkerClick(domEvent, marker);
                            }
                        );
                        if (
                            circleCenter &&
                            circleCenter.coordinates &&
                            circleCenter.coordinates.length === 2 &&
                            circleCenter.coordinates[0] ===
                                marker.latLong.coordinates[0] &&
                            circleCenter.coordinates[1] ===
                                marker.latLong.coordinates[1] &&
                            thisMarker &&
                            thisMarker.content &&
                            thisMarker.content.classList &&
                            thisMarker.content.parentElement &&
                            thisMarker.content.parentElement.parentElement &&
                            thisMarker.content.parentElement.parentElement
                                .classList
                        ) {
                            thisMarker.content.classList.add('selected');
                            thisMarker.content.parentElement.parentElement.classList.add(
                                'markerSelected'
                            );
                        }
                    }
                    return thisMarker;
                }
            });

            mapMarkersRef.current = markersCoords.filter(Boolean);
            setMarkers(true);
        } catch {
            teardown();
            return teardown;
        }

        if (preferredArtists && preferredArtists.length > 0) {
            preferredArtists.forEach((preferredArtist) => {
                var selectThisMarker = document.querySelector(
                    '.marker' + preferredArtist.slug
                );
                // console.log('selectThisMarker', selectThisMarker);
                if (
                    selectThisMarker &&
                    selectThisMarker.parentElement &&
                    selectThisMarker.parentElement.parentElement
                ) {
                    selectThisMarker.classList.add('selected');
                    selectThisMarker.parentElement.parentElement.classList.add(
                        'markerSelected'
                    );
                }
            });
        }

        return teardown;
    }, [markerIdsKey, theMap, offsetOverlapping, markerLibraryReady]);

    useEffect(() => {
        if (!theMap || !offsetOverlapping) {
            return undefined;
        }
        const listener = theMap.addListener('zoom_changed', () => {
            const mapZoom = theMap.getZoom();
            pinOffsetTargetsRef.current.forEach(
                ({ content, marker, slot, lat }) => {
                    applyOverlappingPinOffset(
                        content,
                        marker,
                        slot,
                        mapZoom,
                        lat
                    );
                }
            );
        });
        return () => {
            if (window.google?.maps?.event) {
                window.google.maps.event.removeListener(listener);
            }
        };
    }, [theMap, offsetOverlapping, markersSet]);

    useEffect(() => {
        if (!theMap || !mapRef.current) {
            return undefined;
        }
        const el = mapRef.current;
        if (!(el instanceof Element)) {
            return undefined;
        }
        const triggerResize = () => {
            if (window.google?.maps?.event) {
                window.google.maps.event.trigger(theMap, 'resize');
            }
        };
        // Dialogs often mount the map before layout finishes; resize when the pane grows.
        const raf = requestAnimationFrame(() => triggerResize());
        const ro = new ResizeObserver(() => triggerResize());
        ro.observe(el);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
        };
    }, [theMap]);

    const [radiusCircle, setRadiusCircle] = useState(null);

    useEffect(() => {
        if (!theMap || !window.google?.maps?.Circle) {
            return undefined;
        }
        if (
            !radius ||
            !circleCenter ||
            !Array.isArray(circleCenter.coordinates) ||
            circleCenter.coordinates.length !== 2
        ) {
            if (radiusCircle) {
                radiusCircle.setMap(null);
                setRadiusCircle(null);
                setCircle(false);
            }
            return undefined;
        }
        const miles = Number(radius) || 0;
        const meters = miles * 1609.34;
        const centerLatLng = {
            lat: circleCenter.coordinates[1],
            lng: circleCenter.coordinates[0],
        };
        if (!radiusCircle) {
            const newRadiusCircle = new window.google.maps.Circle({
                center: centerLatLng,
                radius: meters,
                map: theMap,
                strokeColor: '#ffa14e',
                strokeOpacity: 0.85,
                strokeWeight: 2,
                fillColor: '#ffa14e',
                fillOpacity: 0.12,
            });
            setRadiusCircle(newRadiusCircle);
            setCircle(true);
            return undefined;
        }
        radiusCircle.setMap(theMap);
        radiusCircle.setRadius(meters);
        radiusCircle.setCenter(centerLatLng);
        setCircle(true);
        return undefined;
    }, [radius, theMap, circleCenter, radiusCircle]);

    useEffect(() => {
        //change location, move circle, maybe change radius, pan map, and zoom
        if (
            theMap &&
            circleCenter &&
            circleCenter.coordinates.length === 2 &&
            markersSet
        ) {
            theMap.panTo({
                lat: circleCenter.coordinates[1],
                lng: circleCenter.coordinates[0],
            });
            if (radius && theMap.getZoom() < 7) {
                theMap.setZoom(7);
            }
        }
        if (preferredArtists && preferredArtists.length > 0) {
            var elems = document.querySelectorAll('.selected');
            [].forEach.call(elems, function (el) {
                if (el.parentElement && el.parentElement.parentElement) {
                    el.parentElement.parentElement.classList.remove(
                        'markerSelected'
                    );
                }
                el.classList.remove('selected');
            });
            preferredArtists.forEach((preferredArtist) => {
                var selectThisMarker = document.querySelector(
                    '.marker' + preferredArtist.slug
                );
                // console.log('selectThisMarker', selectThisMarker);
                if (
                    selectThisMarker &&
                    selectThisMarker.parentElement &&
                    selectThisMarker.parentElement.parentElement
                ) {
                    selectThisMarker.classList.add('selected');
                    selectThisMarker.parentElement.parentElement.classList.add(
                        'markerSelected'
                    );
                }
            });
        }
    }, [
        theMap,
        circleCenter,
        preferredArtists,
        markersSet,
        radius,
    ]);

    return (
        <div
            ref={mapRef}
            id="map"
            style={{
                marginTop: '20px',
                height: mapHeight || '500px',
                width: '100%',
            }}
        />
    );
};

MyMapComponent.propTypes = {
    center: PropTypes.object,
    zoom: PropTypes.number,
    markers: PropTypes.array,
    markerClick: PropTypes.func,
    radius: PropTypes.number,
    circleCenter: PropTypes.object,
    preferredArtists: PropTypes.array,
    offsetOverlapping: PropTypes.bool,
    mapHeight: PropTypes.string,
    onPinClick: PropTypes.func,
};

const GoogleMapForHosts = ({
    markers,
    markerClick,
    radius,
    circleCenter,
    preferredArtists,
    zoom,
    offsetOverlapping,
    mapHeight,
    onPinClick,
}) => {
    const [authFailed, setAuthFailed] = useState(false);

    useLayoutEffect(() => {
        const previous = window.gm_authFailure;
        window.gm_authFailure = () => {
            setAuthFailed(true);
            if (typeof previous === 'function') {
                previous();
            }
        };
        return () => {
            window.gm_authFailure = previous;
        };
    }, []);

    // useEffect(() => {
    //     console.log('markers', markers);
    // }, [markers]);

    if (authFailed) {
        return <MapFailed />;
    }

    return (
        <>
            <Wrapper
                apiKey={'AIzaSyBb4pAeXW0ouFyFYWVb6dz24PQw2C0GE1A'}
                libraries={['marker']}
                render={render}
            >
                <MapErrorBoundary>
                    <MyMapComponent
                        center={{
                            lat: 36.974,
                            lng: -97.03,
                        }}
                        zoom={zoom || 6}
                        markers={markers}
                        markerClick={markerClick}
                        radius={radius}
                        circleCenter={circleCenter}
                        preferredArtists={preferredArtists}
                        offsetOverlapping={offsetOverlapping}
                        mapHeight={mapHeight}
                        onPinClick={onPinClick}
                    />
                </MapErrorBoundary>
            </Wrapper>
        </>
    );
};

GoogleMapForHosts.propTypes = {
    markers: PropTypes.array,
    markerClick: PropTypes.func,
    radius: PropTypes.number,
    circleCenter: PropTypes.object,
    preferredArtists: PropTypes.array,
    zoom: PropTypes.number,
    offsetOverlapping: PropTypes.bool,
    mapHeight: PropTypes.string,
    onPinClick: PropTypes.func,
};

export default GoogleMapForHosts;
